/*
  Urban Alchemy cart drawer.

  1. Buttons and tick boxes that change the cart. Each one sits beside a small set of instructions
     written by the theme (what to take out of the cart, what to put in). This file carries them out,
     then redraws the drawer. If the new items cannot be added, anything taken out is put back and
     the customer is told.
       - "Add" on the serum suggestion and the eye patches (snippets/ua-cart-suggestion.liquid,
         snippets/ua-cart-companion.liquid)
       - the two tick boxes under the cart items (snippets/ua-cart-options.liquid)
  2. The remove button on a Ritual shown as one row (snippets/ua-cart-bundle-rows.liquid).
  3. Keeping the cart tidy after every change, whichever part of the site made it:
       - two or three serums added separately, as one-time purchases, are combined into their
         Ritual (the drawer's own "Add" suggestion already does this; this covers a second or
         third serum added from its own product page instead)
       - one frequency per Ritual (the subscription app would otherwise charge half)
       - priority dispatch never left in the cart on its own, or on an auto replenishment order
*/
(function () {
  if (window.__uaCartDrawerLoaded) return;
  window.__uaCartDrawerLoaded = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Keep these two in step with snippets/ua-cart-options.liquid
  var DISPATCH_HANDLE = 'priority-dispatch';
  var SWITCH_ATTRIBUTE = 'Auto replenishment chosen in cart';

  // Keep these two in step with snippets/ua-cart-suggestion.liquid and snippets/cart-drawer-wrapper.liquid
  var MERGE_SERUM_HANDLES = ['hydra-catalyst', 'radiance-catalyst', 'renewal-catalyst'];
  var MERGE_DUO_PAIRS = {
    'the-radiance-ritual': ['hydra-catalyst', 'radiance-catalyst'],
    'the-renewal-ritual': ['hydra-catalyst', 'renewal-catalyst'],
    'the-day-and-night-ritual': ['radiance-catalyst', 'renewal-catalyst'],
  };
  var TRIO_HANDLE = 'the-ritual-of-three';
  var MERGE_DATA = (function () {
    var el = document.getElementById('UaMergeData');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  })();

  var working = false; // a button or tick box is part-way through changing the cart
  var checking = false; // the tidy-up is running
  var checkTimer = null;

  function post(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
    });
  }

  function cleanItem(item) {
    var copy = { id: item.id, quantity: item.quantity };
    if (item.selling_plan) copy.selling_plan = item.selling_plan;
    var props = item.properties;
    if (props && typeof props === 'object' && !Array.isArray(props) && Object.keys(props).length) {
      copy.properties = props;
    }
    return copy;
  }

  function redraw() {
    return fetch(root + '?sections=cart-drawer,cart-icon-bubble')
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var parser = new DOMParser();
        var drawer = document.querySelector('cart-drawer');
        var fresh = parser.parseFromString(sections['cart-drawer'] || '', 'text/html');
        var freshDrawer = fresh.querySelector('cart-drawer');
        var inner = drawer && drawer.querySelector('.drawer__inner');
        var freshInner = fresh.querySelector('.drawer__inner');
        if (inner && freshInner) inner.innerHTML = freshInner.innerHTML;
        if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));

        var bubble = document.getElementById('cart-icon-bubble');
        var freshBubble = parser
          .parseFromString(sections['cart-icon-bubble'] || '', 'text/html')
          .querySelector('.shopify-section');
        if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
      });
  }

  /*
    Carries out one set of instructions: { remove: [...], add: [...], attributes: {...} }.
    Takes the listed lines out, puts the new items in, then saves any order note.
    If adding fails, the lines that were taken out are put back and the error is passed on.
  */
  function applyChange(change) {
    var toRemove = change.remove || [];
    var toAdd = (change.add || []).map(cleanItem);
    var removed = false;
    working = true;

    var step = Promise.resolve();
    if (toRemove.length) {
      var updates = {};
      toRemove.forEach(function (line) {
        updates[line.key] = 0;
      });
      step = post('cart/update.js', { updates: updates }).then(function () {
        removed = true;
      });
    }

    return step
      .then(function () {
        return toAdd.length ? post('cart/add.js', { items: toAdd }) : null;
      })
      .then(function () {
        return change.attributes ? post('cart/update.js', { attributes: change.attributes }) : null;
      })
      .catch(function (error) {
        var putBack = removed
          ? post('cart/add.js', {
              items: toRemove.map(function (line) {
                return cleanItem(line.restore);
              }),
            }).catch(function () {})
          : Promise.resolve();
        return putBack.then(function () {
          throw error;
        });
      })
      .then(
        function () {
          working = false;
          checkSoon();
        },
        function (error) {
          working = false;
          checkSoon();
          throw error;
        }
      );
  }

  function readChange(container, selector) {
    var data = container && container.querySelector(selector);
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return null;
    }
  }

  function showMessage(selector, text) {
    document.querySelectorAll('cart-drawer ' + selector).forEach(function (message) {
      message.textContent = text;
      message.hidden = false;
    });
  }

  // "Add" on the serum suggestion and on the eye patches.
  // The offers are printed twice (a side panel on desktop, a block under the items on phones),
  // so the busy state and any message are applied to both copies.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-ua-suggest-add]');
    if (!button || button.disabled) return;
    event.preventDefault();

    var change = readChange(button.closest('.ua-suggest'), 'script[data-ua-suggest]');
    if (!change || !(change.add || []).length) return;

    document.querySelectorAll('cart-drawer .ua-suggest').forEach(function (copy) {
      copy.classList.add('is-loading');
      var copyButton = copy.querySelector('[data-ua-suggest-add]');
      if (copyButton) copyButton.disabled = true;
    });

    applyChange(change)
      .then(redraw)
      .catch(function () {
        return redraw()
          .catch(function () {})
          .then(function () {
            showMessage('.ua-suggest__error', 'This could not be added. The cart is unchanged.');
          });
      });
  });

  // The tick boxes under the cart items: auto replenishment, and priority dispatch.
  document.addEventListener('change', function (event) {
    var box = event.target.closest('[data-ua-option]');
    if (!box) return;

    var change = readChange(box.closest('.ua-option'), 'script[data-ua-change]');
    if (!change) return;

    var options = box.closest('.ua-options');
    if (options) {
      options.classList.add('is-loading');
      options.querySelectorAll('[data-ua-option]').forEach(function (input) {
        input.disabled = true;
      });
    }

    applyChange(change)
      .then(redraw)
      .catch(function () {
        return redraw()
          .catch(function () {})
          .then(function () {
            showMessage('.ua-options__error', 'That change could not be made. The cart is unchanged.');
          });
      });
  });

  // Remove button on a Ritual shown as one row: takes out every serum in that Ritual together.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-ua-remove-lines]');
    if (!button || button.disabled) return;
    event.preventDefault();

    var keys = (button.getAttribute('data-ua-remove-lines') || '').split(',').filter(Boolean);
    if (!keys.length) return;

    button.disabled = true;
    var row = button.closest('.cart-item');
    if (row) row.classList.add('ua-is-removing');

    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });

    post('cart/update.js', { updates: updates })
      .then(redraw)
      .catch(function () {
        return redraw().catch(function () {});
      });
  });

  /*
    Keeping the cart tidy. Runs after every cart change and when a page loads.

    One frequency per Ritual.
      The subscription app prices a Ritual on auto replenishment by the Ritual's name. If the same Ritual
      is in the cart on two frequencies (every 2 months and every 3 months), the app prices the two as one
      and the customer is charged half. So the cart keeps one frequency per Ritual: the one added most
      recently. The earlier one is removed and the customer is told.
      The same Ritual twice on the same frequency is fine; that is simply a quantity of two.

    Priority dispatch.
      It is removed if it is the only thing left in the cart, or if the cart holds an auto replenishment
      item (priority dispatch is already included there). It is never more than a quantity of one.

    The "chosen in cart" order note is cleared if nothing in the cart is on auto replenishment any more.
  */
  function showNote(text) {
    var items = document.querySelector('cart-drawer cart-drawer-items');
    if (!items) return;
    var note = document.createElement('p');
    note.className = 'ua-cart-note';
    note.setAttribute('role', 'status');
    note.textContent = text;
    items.insertBefore(note, items.firstChild);
  }

  // "Auto Replenishment, Every 2 months" -> "every 2 months"
  function frequency(planName) {
    return String(planName || '').split(',').pop().trim().toLowerCase();
  }

  /*
    Two or three serums, each added as a separate one-time line (from a product page, say,
    rather than the drawer's own "Add" suggestion), become their Ritual. A Ritual already in the
    cart plus the one serum it is missing becomes the trio. Only plain one-time lines are
    touched: a serum on auto replenishment, or already part of a Ritual's own tagged lines, is
    left alone.
    Returns a { remove, add, note } change, or null if nothing to merge.
  */
  function findMerge(cart) {
    if (!MERGE_DATA) return null;

    var serumLines = {};
    var duoLine = null;
    var clean = true;

    (cart.items || []).forEach(function (item) {
      if (item.handle === DISPATCH_HANDLE) return;
      if (item.selling_plan_allocation) return;
      if (item.properties && item.properties.__bundle_variant_id) return;

      if (MERGE_SERUM_HANDLES.indexOf(item.handle) !== -1) {
        if (serumLines[item.handle] || item.quantity !== 1) {
          clean = false;
          return;
        }
        serumLines[item.handle] = item;
      } else if (MERGE_DUO_PAIRS[item.handle]) {
        if (duoLine || item.quantity !== 1) {
          clean = false;
          return;
        }
        duoLine = item;
      }
    });

    if (!clean) return null;
    var serumHandles = Object.keys(serumLines);

    function toChange(lines, variantId) {
      return {
        remove: lines.map(function (line) {
          return { key: line.key, restore: { id: line.variant_id, quantity: line.quantity } };
        }),
        add: [{ id: variantId, quantity: 1 }],
      };
    }

    if (duoLine) {
      if (serumHandles.length !== 1) return null;
      var missing = serumHandles[0];
      var pair = MERGE_DUO_PAIRS[duoLine.handle];
      if (pair.indexOf(missing) !== -1) return null; // duplicates a serum already in the duo
      var trioId = MERGE_DATA.trio;
      if (!trioId) return null;
      var change = toChange([duoLine, serumLines[missing]], trioId);
      change.note =
        duoLine.product_title + ' and ' + serumLines[missing].product_title + ' combined into ' + MERGE_DATA.titles[TRIO_HANDLE] + '.';
      return change;
    }

    if (serumHandles.length === 2) {
      var key = serumHandles.slice().sort().join('|');
      var duoVariantId = MERGE_DATA.duos[key];
      if (!duoVariantId) return null;
      var duoHandle = Object.keys(MERGE_DUO_PAIRS).filter(function (handle) {
        return MERGE_DUO_PAIRS[handle].slice().sort().join('|') === key;
      })[0];
      var lines2 = [serumLines[serumHandles[0]], serumLines[serumHandles[1]]];
      var change2 = toChange(lines2, duoVariantId);
      change2.note = lines2[0].product_title + ' and ' + lines2[1].product_title + ' combined into ' + MERGE_DATA.titles[duoHandle] + '.';
      return change2;
    }

    if (serumHandles.length === 3) {
      var trioVariantId = MERGE_DATA.trio;
      if (!trioVariantId) return null;
      var lines3 = serumHandles.map(function (handle) {
        return serumLines[handle];
      });
      var change3 = toChange(lines3, trioVariantId);
      change3.note = 'Hydra Catalyst, Radiance Catalyst and Renewal Catalyst combined into ' + MERGE_DATA.titles[TRIO_HANDLE] + '.';
      return change3;
    }

    return null;
  }

  function tidyCart() {
    if (checking || working) return Promise.resolve(false);
    checking = true;
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var merge = findMerge(cart);
        if (merge) {
          return applyChange(merge)
            .then(redraw)
            .then(function () {
              showNote(merge.note);
              checkSoon();
              return true;
            });
        }

        var kept = {};
        var updates = {};
        var replaced = null;
        var dispatch = null;
        var products = 0;
        var onPlan = false;

        // Cart lines come newest first, so the first frequency seen for a Ritual is the latest one.
        (cart.items || []).forEach(function (item) {
          if (item.handle === DISPATCH_HANDLE) {
            dispatch = item;
            return;
          }
          products += 1;
          var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
          if (plan) onPlan = true;
          var ritual = item.properties && item.properties.__bundle_variant_id;
          if (!ritual || !plan) return;
          if (!kept[ritual]) {
            kept[ritual] = plan;
            return;
          }
          if (kept[ritual].id !== plan.id) {
            updates[item.key] = 0;
            replaced = {
              name: item.properties.__bundle_name || item.properties.Bundle || 'This Ritual',
              plan: frequency(kept[ritual].name),
              old: frequency(plan.name),
            };
          }
        });

        if (dispatch) {
          if (!products || onPlan) updates[dispatch.key] = 0;
          else if (dispatch.quantity > 1) updates[dispatch.key] = 1;
        }

        var body = {};
        var linesChanged = Object.keys(updates).length > 0;
        if (linesChanged) body.updates = updates;
        if (!onPlan && cart.attributes && cart.attributes[SWITCH_ATTRIBUTE]) {
          body.attributes = {};
          body.attributes[SWITCH_ATTRIBUTE] = '';
        }
        if (!body.updates && !body.attributes) return false;

        return post('cart/update.js', body).then(function () {
          if (!linesChanged) return true;
          if (/\/cart\/?$/.test(window.location.pathname)) {
            window.location.reload();
            return true;
          }
          return redraw().then(function () {
            if (replaced) {
              showNote(
                replaced.name +
                  ' is now set to ' +
                  (replaced.plan || 'the new frequency') +
                  (replaced.old ? ', replacing ' + replaced.old : '') +
                  '.'
              );
            }
            return true;
          });
        });
      })
      .catch(function () {
        return false;
      })
      .then(function (result) {
        checking = false;
        return result;
      });
  }

  function checkSoon() {
    if (checking || working) return;
    clearTimeout(checkTimer);
    checkTimer = setTimeout(tidyCart, 200);
  }

  // Run the tidy-up whenever anything on the page changes the cart, whichever script does it.
  var cartChange = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var result = nativeFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (cartChange.test(url)) result.then(checkSoon, function () {});
      } catch (error) {}
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (cartChange.test(String(url))) this.addEventListener('loadend', checkSoon);
      } catch (error) {}
      return nativeOpen.apply(this, arguments);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkSoon);
  } else {
    checkSoon();
  }
})();
